import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from '../../../utility/axios';
import { useTableChanges } from '../../../hooks/useWebSocket';
import { Button, message, Spin, Input, Badge, Avatar, Tag, Checkbox, Select, Dropdown, Space } from 'antd';
import { SearchOutlined, UserOutlined, MailOutlined, CalendarOutlined, PlusOutlined, DeleteOutlined, SaveOutlined, RollbackOutlined, CheckCircleOutlined, DownOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../hooks/useAuth';
import AddUserModal from './AddUserModal';
import '../../../styles/settings/settings-common.css';
import '../../../styles/settings/user-management.css';

const UserManagement = ({ embedded, unifiedPage }) => {
    useAuth();
    const { t } = useTranslation();
    const [users, setUsers] = useState([]);
    const [registrations, setRegistrations] = useState([]);
    const [deletedUsers, setDeletedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectedRegistrations, setSelectedRegistrations] = useState([]);
    const [selectedDeletedUsers, setSelectedDeletedUsers] = useState([]);
    const [addUserModalVisible, setAddUserModalVisible] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [roles, setRoles] = useState([]);
    const [editFormData, setEditFormData] = useState({
        firstname: '',
        lastname: '',
        status: '',
        role: '',
        role_id: null
    });
    // Get the initial tab from localStorage or default to 'users'
    const getInitialTab = () => {
        const savedTab = localStorage.getItem('userManagementActiveTab');
        return savedTab && ['users', 'registrations', 'deletedUsers'].includes(savedTab) ? savedTab : 'users';
    };

    const getInitialSortConfig = () => {
        try {
            const saved = localStorage.getItem('userManagementSortConfig');
            if (saved) {
                const parsed = JSON.parse(saved);
                const validKeys = ['name', 'email', 'role', 'created_at'];
                if (validKeys.includes(parsed.key) && ['ascend', 'descend'].includes(parsed.direction)) {
                    return parsed;
                }
            }
        } catch (_) {}
        return { key: null, direction: 'ascend' };
    };
    
    const [activeTab, setActiveTab] = useState(getInitialTab);
    const [sortConfig, setSortConfig] = useState(getInitialSortConfig);
    const [userEdits, setUserEdits] = useState({});

    // Available tabs configuration
    const availableTabs = [
        { id: 'users', label: t('admin.userManagement.users'), count: users.length, badgeColor: '#5E5BFF' },
        { id: 'registrations', label: t('admin.userManagement.registrations'), count: registrations.length, badgeColor: '#faad14' },
        { id: 'deletedUsers', label: t('admin.userManagement.deletedUsers'), count: deletedUsers.length, badgeColor: '#ff4d4f' },
    ];

    // Handle tab change
    const handleTabChange = (tabId) => {
        setActiveTab(tabId);
        setSelectedUser(null);
        setSearchText('');
        setSelectedUsers([]);
        setSelectedRegistrations([]);
        setSelectedDeletedUsers([]);
        setUserEdits({});
        localStorage.setItem('userManagementActiveTab', tabId);
    };

    const getUserEditValue = (record, field) => {
        const id = record.unique_identifier;
        if (userEdits[id] && field in userEdits[id]) return userEdits[id][field];
        if (field === 'role_id') {
            const r = record.role && roles.length > 0 ? roles.find(rr => rr.name.toLowerCase() === (record.role || '').toLowerCase()) : null;
            return r ? r.id : null;
        }
        return (record[field] ?? '').toString();
    };

    const handleUserFieldChange = (uniqueId, field, value) => {
        setUserEdits(prev => ({
            ...prev,
            [uniqueId]: {
                ...(prev[uniqueId] || {}),
                [field]: value
            }
        }));
    };

    // Handle user selection
    const handleUserSelect = (record, event) => {
        // If Ctrl or Cmd is held, toggle checkbox instead
        if (event && (event.ctrlKey || event.metaKey)) {
            const currentSelected = getCurrentSelected();
            const isSelected = currentSelected.includes(record.unique_identifier);
            
            const setSelection = activeTab === 'users' ? setSelectedUsers :
                               activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
            
            if (isSelected) {
                setSelection(prev => prev.filter(id => id !== record.unique_identifier));
            } else {
                setSelection(prev => [...prev, record.unique_identifier]);
            }
        } else {
            setSelectedUser(record);
            const setSelection = activeTab === 'users' ? setSelectedUsers :
                               activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
            setSelection([record.unique_identifier]);
            // Initialize edit form data when selecting a user
            let userRole = null;
            if (record.role && roles.length > 0) {
                userRole = roles.find(r => r.name.toLowerCase() === (record.role || '').toLowerCase());
            }
            setEditFormData({
                firstname: record.firstname,
                lastname: record.lastname,
                status: record.status || 'Active',
                role: record.role || '',
                role_id: userRole ? userRole.id : null
            });
        }
    };

    // Handle checkbox selection
    const handleCheckboxChange = (recordId, checked, e) => {
        e.stopPropagation();
        
        const setSelection = activeTab === 'users' ? setSelectedUsers :
                           activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
        
        if (checked) {
            setSelection(prev => [...prev, recordId]);
        } else {
            setSelection(prev => prev.filter(id => id !== recordId));
        }
    };

    // Get current selected array
    const getCurrentSelected = () => {
        switch (activeTab) {
            case 'users':
                return selectedUsers;
            case 'registrations':
                return selectedRegistrations;
            case 'deletedUsers':
                return selectedDeletedUsers;
            default:
                return [];
        }
    };

    // Handle batch delete
    const handleBatchDelete = async () => {
        const currentSelected = getCurrentSelected();
        
        if (currentSelected.length === 0) {
            message.warning(t('admin.userManagement.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            if (activeTab === 'users') {
                await axios.post('/api/admin/users/delete/', { userIds: currentSelected });
                await Promise.all([fetchUsers(true), fetchDeletedUsers(true)]);
                setSelectedUsers([]);
                message.success(t('admin.userManagement.successDeleted', { count: currentSelected.length }));
            } else if (activeTab === 'registrations') {
                await axios.post('/api/admin/registrations/revoke/', { userIds: currentSelected });
                await fetchRegistrations(true);
                setSelectedRegistrations([]);
                message.success(t('admin.userManagement.successRevoked', { count: currentSelected.length }));
            } else if (activeTab === 'deletedUsers') {
                await axios.post('/api/admin/users/remove/', { userIds: currentSelected });
                await fetchDeletedUsers(true);
                setSelectedDeletedUsers([]);
                message.success(t('admin.userManagement.successRemoved', { count: currentSelected.length }));
            }
        } catch (error) {
            console.error('Error deleting users:', error);
            message.error(t('admin.userManagement.failedDelete'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch restore
    const handleBatchRestore = async () => {
        const currentSelected = getCurrentSelected();
        
        if (currentSelected.length === 0) {
            message.warning(t('admin.userManagement.selectAtLeastOne'));
            return;
        }

        setLoading(true);
        try {
            await axios.post('/api/admin/users/restore/', { userIds: currentSelected });
            await Promise.all([fetchUsers(true), fetchDeletedUsers(true)]);
            setSelectedDeletedUsers([]);
            message.success(t('admin.userManagement.successRestored', { count: currentSelected.length }));
        } catch (error) {
            console.error('Error restoring users:', error);
            message.error(t('admin.userManagement.failedRestore'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch approve registrations
    const handleBatchApprove = async () => {
        const currentSelected = getCurrentSelected();
        
        if (currentSelected.length === 0) {
            message.warning(t('admin.userManagement.selectAtLeastOneRegistration'));
            return;
        }

        setLoading(true);
        try {
            await axios.post('/api/admin/registrations/approve/', { userIds: currentSelected });
            await Promise.all([fetchUsers(true), fetchRegistrations(true)]);
            setSelectedRegistrations([]);
            message.success(t('admin.userManagement.successApproved', { count: currentSelected.length }));
        } catch (error) {
            console.error('Error approving registrations:', error);
            message.error(t('admin.userManagement.failedApprove'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch revoke registrations
    const handleBatchRevoke = async () => {
        const currentSelected = getCurrentSelected();
        
        if (currentSelected.length === 0) {
            message.warning(t('admin.userManagement.selectAtLeastOneRegistration'));
            return;
        }

        setLoading(true);
        try {
            await axios.post('/api/admin/registrations/revoke/', { userIds: currentSelected });
            await fetchRegistrations(true);
            setSelectedRegistrations([]);
            message.success(t('admin.userManagement.successRevoked', { count: currentSelected.length }));
        } catch (error) {
            console.error('Error revoking registrations:', error);
            message.error(t('admin.userManagement.failedRevoke'));
        } finally {
            setLoading(false);
        }
    };

    // Handle batch resend invitations
    const handleBatchResendInvite = async () => {
        const currentSelected = getCurrentSelected();
        
        if (currentSelected.length === 0) {
            message.warning(t('admin.userManagement.selectAtLeastOneRegistration'));
            return;
        }

        setLoading(true);
        try {
            await axios.post('/api/admin/registrations/resendInvite/', { userIds: currentSelected });
            message.success(t('admin.userManagement.successResent', { count: currentSelected.length }));
            setSelectedRegistrations([]);
        } catch (error) {
            console.error('Error resending invitations:', error);
            message.error(t('admin.userManagement.failedResend'));
        } finally {
            setLoading(false);
        }
    };

    // Handle single user delete
    const handleDeleteUser = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await axios.post('/api/admin/users/delete/', { userIds: [selectedUser.unique_identifier] });
            await Promise.all([fetchUsers(true), fetchDeletedUsers(true)]);
            setSelectedUser(null);
            message.success(t('admin.userManagement.successDeleted', { count: 1 }));
        } catch (error) {
            console.error('Error deleting user:', error);
            message.error(t('admin.userManagement.failedDelete'));
        } finally {
            setLoading(false);
        }
    };

    // Handle single user restore
    const handleRestoreUser = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await axios.post('/api/admin/users/restore/', { userIds: [selectedUser.unique_identifier] });
            await Promise.all([fetchUsers(true), fetchDeletedUsers(true)]);
            setSelectedUser(null);
            message.success(t('admin.userManagement.successRestored', { count: 1 }));
        } catch (error) {
            console.error('Error restoring user:', error);
            message.error(t('admin.userManagement.failedRestore'));
        } finally {
            setLoading(false);
        }
    };

    // Handle single user permanent delete
    const handlePermanentDeleteUser = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await axios.post('/api/admin/users/remove/', { userIds: [selectedUser.unique_identifier] });
            await fetchDeletedUsers(true);
            setSelectedUser(null);
            message.success(t('admin.userManagement.successRemoved', { count: 1 }));
        } catch (error) {
            console.error('Error permanently deleting user:', error);
            message.error(t('admin.userManagement.failedDelete'));
        } finally {
            setLoading(false);
        }
    };

    // Handle single registration approve
    const handleApproveRegistration = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await axios.post('/api/admin/registrations/approve/', { userIds: [selectedUser.unique_identifier] });
            await Promise.all([fetchUsers(true), fetchRegistrations(true)]);
            setSelectedUser(null);
            message.success(t('admin.userManagement.successApproved', { count: 1 }));
        } catch (error) {
            console.error('Error approving registration:', error);
            message.error(t('admin.userManagement.failedApprove'));
        } finally {
            setLoading(false);
        }
    };

    // Handle single registration revoke
    const handleRevokeRegistration = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await axios.post('/api/admin/registrations/revoke/', { userIds: [selectedUser.unique_identifier] });
            await fetchRegistrations(true);
            setSelectedUser(null);
            message.success(t('admin.userManagement.successRevoked', { count: 1 }));
        } catch (error) {
            console.error('Error revoking registration:', error);
            message.error(t('admin.userManagement.failedRevoke'));
        } finally {
            setLoading(false);
        }
    };

    // Handle single resend invite
    const handleResendInvite = async () => {
        if (!selectedUser) return;
        
        setLoading(true);
        try {
            await axios.post('/api/admin/registrations/resendInvite/', { userIds: [selectedUser.unique_identifier] });
            message.success(t('admin.userManagement.successResent', { count: 1 }));
        } catch (error) {
            console.error('Error resending invitation:', error);
            message.error(t('admin.userManagement.failedResend'));
        } finally {
            setLoading(false);
        }
    };

    const handleAddUser = () => {
        setEditingUser(null);
        setAddUserModalVisible(true);
    };

    // Handle user created
    const handleUserCreated = async (newUser) => {
        // Refresh the users list
        await fetchUsers(true);
    };

    // Handle field update with auto-save
    const handleSaveChanges = async () => {
        if (embedded && activeTab === 'users') {
            const keysToSave = Object.keys(userEdits).filter(k => users.some(u => u.unique_identifier === k));
            if (keysToSave.length === 0) return;
            setLoading(true);
            try {
                for (const uniqueId of keysToSave) {
                    const record = users.find(u => u.unique_identifier === uniqueId);
                    if (!record) continue;
                    const edits = userEdits[uniqueId] || {};
                    const firstname = (edits.firstname ?? record.firstname ?? '').toString();
                    const lastname = (edits.lastname ?? record.lastname ?? '').toString();
                    if (!firstname || !lastname) {
                        message.error(t('admin.userManagement.firstNameRequired'));
                        return;
                    }
                    const roleId = edits.role_id ?? (roles.find(r => r.name.toLowerCase() === (record.role || '').toLowerCase())?.id);
                    if (!roleId) {
                        message.error(t('admin.userManagement.roleRequired'));
                        return;
                    }
                    await axios.post('/api/admin/users/update/', {
                        unique_identifier: uniqueId,
                        firstname,
                        lastname,
                        status: edits.status ?? record.status ?? 'Active',
                        role_id: roleId
                    });
                }
                setUserEdits({});
                await fetchUsers(true);
                message.success(t('admin.userManagement.userUpdated'));
            } catch (error) {
                console.error('Error updating user:', error);
                message.error(error.response?.data?.error || t('admin.userManagement.failedUpdate'));
            } finally {
                setLoading(false);
            }
            return;
        }

        if (!editFormData.firstname || !editFormData.lastname) {
            message.error(t('admin.userManagement.firstNameRequired'));
            return;
        }

        if (!editFormData.role_id) {
            message.error(t('admin.userManagement.roleRequired'));
            return;
        }

        setLoading(true);
        try {
            await axios.post('/api/admin/users/update/', {
                unique_identifier: selectedUser.unique_identifier,
                firstname: editFormData.firstname,
                lastname: editFormData.lastname,
                status: editFormData.status,
                role_id: editFormData.role_id
            });

            setUsers(prevUsers => 
                prevUsers.map(u => 
                    u.unique_identifier === selectedUser.unique_identifier 
                        ? { ...u, ...editFormData }
                        : u
                )
            );

            setSelectedUser(prev => ({ ...prev, ...editFormData }));

            message.success(t('admin.userManagement.userUpdated'));
        } catch (error) {
            console.error('Error updating user:', error);
            message.error(error.response?.data?.error || t('admin.userManagement.failedUpdate'));
        } finally {
            setLoading(false);
        }
    };

    const hasEdits = activeTab === 'users' && Object.keys(userEdits).some(k => users.some(u => u.unique_identifier === k));

    const handleFieldChange = (field, value) => {
        // Update local form data immediately for responsive UI
        setEditFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Filter data based on search text
    const filterData = (data) => {
        if (!searchText) return data;
        
        return data.filter(record => {
            const email = record.email.toLowerCase();
            const name = `${record.firstname} ${record.lastname}`.toLowerCase();
            const searchLower = searchText.toLowerCase();
            
            return email.includes(searchLower) || name.includes(searchLower);
        });
    };

    // Get current data based on active tab
    const getCurrentData = () => {
        switch (activeTab) {
            case 'users':
                return filterData(users);
            case 'registrations':
                return filterData(registrations);
            case 'deletedUsers':
                return filterData(deletedUsers);
            default:
                return [];
        }
    };

    const sortUserManagementData = (data, config) => {
        if (!config.key) return data;
        const dir = config.direction === 'ascend' ? 1 : -1;
        return [...data].sort((a, b) => {
            let va, vb;
            switch (config.key) {
                case 'name':
                    va = `${a.firstname || ''} ${a.lastname || ''}`.trim().toLowerCase();
                    vb = `${b.firstname || ''} ${b.lastname || ''}`.trim().toLowerCase();
                    return dir * va.localeCompare(vb);
                case 'email':
                    va = (a.email || '').toLowerCase();
                    vb = (b.email || '').toLowerCase();
                    return dir * va.localeCompare(vb);
                case 'role':
                    va = (a.role || '').toLowerCase();
                    vb = (b.role || '').toLowerCase();
                    return dir * va.localeCompare(vb);
                case 'created_at':
                    va = a.created_at ? new Date(a.created_at).getTime() : (a.registered_at ? new Date(a.registered_at).getTime() : 0);
                    vb = b.created_at ? new Date(b.created_at).getTime() : (b.registered_at ? new Date(b.registered_at).getTime() : 0);
                    return dir * (va - vb);
                default:
                    return 0;
            }
        });
    };

    const currentData = getCurrentData();
    const sortedData = useMemo(() => sortUserManagementData(currentData, sortConfig), [currentData, sortConfig]);

    const sortMenuItems = [
        { key: 'name', icon: UserOutlined, labelKey: 'sortByName' },
        { key: 'email', icon: MailOutlined, labelKey: 'sortByEmail' },
        { key: 'role', icon: UserOutlined, labelKey: 'sortByRole' },
        { key: 'created_at', icon: CalendarOutlined, labelKey: 'sortByAddedAt' },
    ];
    const sortDropdown = (
        <div className="um-sort-dropdown">
            <Dropdown
                menu={{
                    items: sortMenuItems.map(({ key, icon: Icon, labelKey }) => ({
                        key,
                        label: (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <span><Icon style={{ marginRight: '8px' }} />{t(`admin.userManagement.${labelKey}`)}</span>
                                {sortConfig.key === key && (sortConfig.direction === 'ascend' ? '↑' : '↓')}
                            </div>
                        ),
                        onClick: () => {
                            const next = { key, direction: sortConfig.key === key && sortConfig.direction === 'ascend' ? 'descend' : 'ascend' };
                            setSortConfig(next);
                            localStorage.setItem('userManagementSortConfig', JSON.stringify(next));
                        }
                    }))
                }}
                trigger={['click']}
            >
                <Button style={{ width: '100%' }}>
                    <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                        <span>
                            {sortConfig.key === null ? t('admin.userManagement.sortBy') : (
                                <>{t(`admin.userManagement.sortBy${sortConfig.key === 'name' ? 'Name' : sortConfig.key === 'email' ? 'Email' : sortConfig.key === 'role' ? 'Role' : 'AddedAt'}`)} {sortConfig.direction === 'ascend' ? '↑' : '↓'}</>
                            )}
                        </span>
                        <DownOutlined />
                    </Space>
                </Button>
            </Dropdown>
        </div>
    );

    const fetchUsers = async (skipLoading = false) => {
        if (!skipLoading) setLoading(true);
        try {
            const response = await axios.get('/api/admin/users/');
            setUsers(response.data.map(item => ({
                ...item,
                key: item.unique_identifier
            })));
        } catch (error) {
            console.error('Error fetching users:', error);
            message.error(t('admin.userManagement.failedToFetch'));
        } finally {
            if (!skipLoading) setLoading(false);
        }
    };

    const fetchRegistrations = async (skipLoading = false) => {
        if (!skipLoading) setLoading(true);
        try {
            const response = await axios.get('/api/admin/registrations/');
            setRegistrations(response.data.map(item => ({
                ...item,
                key: item.unique_identifier
            })));
        } catch (error) {
            console.error('Error fetching registrations:', error);
            message.error(t('admin.userManagement.failedToFetchRegistrations'));
        } finally {
            if (!skipLoading) setLoading(false);
        }
    };

    const fetchDeletedUsers = async (skipLoading = false) => {
        if (!skipLoading) setLoading(true);
        try {
            const response = await axios.get('/api/admin/deleted-users/');
            setDeletedUsers(response.data.map(item => ({
                ...item,
                key: item.unique_identifier
            })));
        } catch (error) {
            console.error('Error fetching deleted users:', error);
            message.error(t('admin.userManagement.failedToFetchDeletedUsers'));
        } finally {
            if (!skipLoading) setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await axios.get('/api/admin/roles/');
            setRoles(response.data || []);
        } catch (error) {
            console.error('Error fetching roles:', error);
            message.error(t('admin.userManagement.failedToLoadRoles'));
        }
    };

    const handleProfilesChange = useCallback(() => {
        Promise.all([fetchUsers(true), fetchRegistrations(true), fetchDeletedUsers(true)]);
    }, []);

    useTableChanges('profiles', handleProfilesChange);

    useEffect(() => {
        const fetchData = async () => {
            setInitialLoading(true);
            try {
                await Promise.all([fetchUsers(), fetchRegistrations(), fetchDeletedUsers(), fetchRoles()]);
            } finally {
                setInitialLoading(false);
            }
        };
        
        fetchData();

        // Real-time updates have been removed
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (initialLoading || embedded) return;
        const currentData = getCurrentData();
        const selectedInTab = selectedUser && currentData.some(r => r.unique_identifier === selectedUser.unique_identifier);

        if (currentData.length > 0 && (!selectedUser || !selectedInTab)) {
            const firstRecord = currentData[0];
            setSelectedUser(firstRecord);
            const setSelection = activeTab === 'users' ? setSelectedUsers :
                               activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
            setSelection([firstRecord.unique_identifier]);
            let userRole = null;
            if (firstRecord.role && roles.length > 0) {
                userRole = roles.find(r => r.name.toLowerCase() === (firstRecord.role || '').toLowerCase());
            }
            setEditFormData({
                firstname: firstRecord.firstname,
                lastname: firstRecord.lastname,
                status: firstRecord.status || 'Active',
                role: firstRecord.role || '',
                role_id: userRole ? userRole.id : null
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLoading, activeTab, users, registrations, deletedUsers, searchText]);

    // Get initials from name
    const getInitials = (firstname, lastname) => {
        const first = firstname ? firstname.charAt(0) : '';
        const last = lastname ? lastname.charAt(0) : '';
        return `${first}${last}`.toUpperCase() || 'U';
    };

    // Get avatar color based on name
    const getAvatarColor = (name) => {
        const colors = ['#5E5BFF', '#FF5E5B', '#FFCE5E', '#5EFFCE', '#CE5EFF', '#5E9EFF'];
        const charCode = name ? name.charCodeAt(0) : 0;
        return colors[charCode % colors.length];
    };

    // Capitalize first letter (camel case)
    const capitalize = (str) => {
        if (!str) return '';
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    };

    if (initialLoading) {
        return (
            <div className="um-container" data-settings-scroll>
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                    <Spin size="large" />
                </div>
            </div>
        );
    }

    const hasSelection = getCurrentSelected().length > 0;
    const selectionCount = getCurrentSelected().length;

    return (
        <div className="um-container" data-settings-scroll>
            {/* Tabs + actions row */}
            <div className="um-top-bar">
                <div className="um-nav-buttons">
                    {availableTabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`um-nav-btn ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => handleTabChange(tab.id)}
                        >
                            <span>{tab.label}</span>
                            <Badge 
                                count={tab.count} 
                                showZero
                                style={{ 
                                    marginLeft: 4,
                                    backgroundColor: tab.badgeColor,
                                    boxShadow: 'none'
                                }}
                            />
                        </button>
                    ))}
                </div>
                <div className="um-nav-actions">
                    {activeTab === 'registrations' && (
                        <>
                            {selectionCount >= 2 ? (
                                <Badge count={selectionCount} size="small" offset={[-2, 2]}>
                                    <Button 
                                        type="primary"
                                        icon={<CheckCircleOutlined />}
                                        onClick={handleBatchApprove}
                                        className="um-action-btn um-action-btn-icon um-action-btn-approve"
                                    />
                                </Badge>
                            ) : (
                                <Button 
                                    type="primary"
                                    icon={<CheckCircleOutlined />}
                                    onClick={handleBatchApprove}
                                    className="um-action-btn um-action-btn-icon um-action-btn-approve"
                                />
                            )}
                            {selectionCount >= 2 ? (
                                <Badge count={selectionCount} size="small" offset={[-2, 2]}>
                                    <Button 
                                        type="primary"
                                        icon={<MailOutlined />}
                                        onClick={handleBatchResendInvite}
                                        className="um-action-btn um-action-btn-icon"
                                    />
                                </Badge>
                            ) : (
                                <Button 
                                    type="primary"
                                    icon={<MailOutlined />}
                                    onClick={handleBatchResendInvite}
                                    className="um-action-btn um-action-btn-icon"
                                />
                            )}
                            {selectionCount >= 2 ? (
                                <Badge count={selectionCount} size="small" offset={[-2, 2]}>
                                    <Button 
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleBatchRevoke}
                                        className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                    />
                                </Badge>
                            ) : (
                                <Button 
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleBatchRevoke}
                                    className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                />
                            )}
                        </>
                    )}
                    {activeTab === 'deletedUsers' && (
                        <>
                            {hasSelection ? (
                                selectionCount >= 2 ? (
                                    <Badge count={selectionCount} size="small" offset={[-2, 2]}>
                                        <Button 
                                            type="primary"
                                            icon={<RollbackOutlined />}
                                            onClick={handleBatchRestore}
                                            className="um-action-btn um-action-btn-icon um-action-btn-restore"
                                        />
                                    </Badge>
                                ) : (
                                    <Button 
                                        type="primary"
                                        icon={<RollbackOutlined />}
                                        onClick={handleBatchRestore}
                                        className="um-action-btn um-action-btn-icon um-action-btn-restore"
                                    />
                                )
                            ) : (
                                <Button 
                                    type="primary"
                                    icon={<RollbackOutlined />}
                                    onClick={handleBatchRestore}
                                    className="um-action-btn um-action-btn-icon um-action-btn-restore"
                                />
                            )}
                            {hasSelection ? (
                                selectionCount >= 2 ? (
                                    <Badge count={selectionCount} size="small" offset={[-2, 2]}>
                                        <Button 
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleBatchDelete}
                                            className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                        />
                                    </Badge>
                                ) : (
                                    <Button 
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleBatchDelete}
                                        className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                    />
                                )
                            ) : (
                                <Button 
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleBatchDelete}
                                    className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                />
                            )}
                        </>
                    )}
                    {activeTab === 'users' && (
                        <>
                            {hasSelection ? (
                                selectionCount >= 2 ? (
                                    <Badge count={selectionCount} size="small" offset={[-2, 2]}>
                                        <Button 
                                            danger
                                            icon={<DeleteOutlined />}
                                            onClick={handleBatchDelete}
                                            className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                        />
                                    </Badge>
                                ) : (
                                    <Button 
                                        danger
                                        icon={<DeleteOutlined />}
                                        onClick={handleBatchDelete}
                                        className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                    />
                                )
                            ) : (
                                <Button 
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={handleBatchDelete}
                                    className="um-action-btn um-action-btn-icon um-action-btn-destructive"
                                />
                            )}
                            <Button 
                                type="primary"
                                icon={<SaveOutlined />}
                                onClick={handleSaveChanges}
                                loading={loading}
                                disabled={embedded ? !hasEdits : !selectedUser}
                                className="um-action-btn um-action-btn-icon btn-save"
                                aria-label={t('admin.userManagement.saveChanges')}
                            />
                        </>
                    )}
                </div>
            </div>

            <div className={embedded ? 'um-cards-container' : 'um-split-container'}>
                {embedded ? (
                    <>
                        <div className="um-search-row um-cards-search-row">
                            <Checkbox
                                checked={sortedData.length > 0 && sortedData.every(r => getCurrentSelected().includes(r.unique_identifier))}
                                indeterminate={sortedData.some(r => getCurrentSelected().includes(r.unique_identifier)) && !sortedData.every(r => getCurrentSelected().includes(r.unique_identifier))}
                                onChange={(e) => {
                                    const setSelection = activeTab === 'users' ? setSelectedUsers : activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
                                    setSelection(e.target.checked ? sortedData.map(r => r.unique_identifier) : []);
                                }}
                                className="um-select-all-checkbox"
                            />
                            <Input
                                placeholder={t('admin.userManagement.searchUsers')}
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                className="um-search-input"
                            />
                            {sortDropdown}
                            {activeTab === 'users' && (
                                <Button type="primary" icon={<PlusOutlined />} onClick={handleAddUser} className="um-add-btn-inline" aria-label={t('admin.userManagement.newUser')} />
                            )}
                        </div>
                        <Spin spinning={loading}>
                            <div className={unifiedPage ? 'settings-records-container' : 'um-cards-inner'} onWheel={unifiedPage ? (e) => e.stopPropagation() : undefined}>
                                {sortedData.length === 0 ? (
                                    <div className="um-empty um-cards-empty">
                                        <UserOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                        <p>{t('admin.userManagement.noUsersFound')}</p>
                                    </div>
                                ) : (
                                    <div className="um-cards-grid">
                                    {sortedData.map((record) => (
                                        <div
                                            key={record.unique_identifier}
                                            className={`um-record-card ${activeTab === 'users' ? 'um-record-card-editable ' : ''}${getCurrentSelected().includes(record.unique_identifier) ? 'selected' : ''}`}
                                            onClick={(e) => { if (!e.target.closest('.um-record-card-checkbox') && !e.target.closest('input') && !e.target.closest('.ant-select')) handleUserSelect(record, e); }}
                                        >
                                            <Checkbox
                                                checked={getCurrentSelected().includes(record.unique_identifier)}
                                                onChange={(e) => handleCheckboxChange(record.unique_identifier, e.target.checked, e)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="um-record-card-checkbox"
                                            />
                                            <Avatar style={{ backgroundColor: getAvatarColor(record.firstname) }} size={40}>
                                                {getInitials(activeTab === 'users' ? getUserEditValue(record, 'firstname') : record.firstname, activeTab === 'users' ? getUserEditValue(record, 'lastname') : record.lastname)}
                                            </Avatar>
                                            {activeTab === 'users' ? (
                                                <div className="um-record-card-fields um-record-card-fields--single-line">
                                                    <Input
                                                        value={getUserEditValue(record, 'firstname')}
                                                        onChange={(e) => handleUserFieldChange(record.unique_identifier, 'firstname', e.target.value)}
                                                        placeholder={t('admin.userManagement.firstName')}
                                                        className="um-record-card-input"
                                                        bordered={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <Input
                                                        value={getUserEditValue(record, 'lastname')}
                                                        onChange={(e) => handleUserFieldChange(record.unique_identifier, 'lastname', e.target.value)}
                                                        placeholder={t('admin.userManagement.lastName')}
                                                        className="um-record-card-input"
                                                        bordered={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                    />
                                                    <div className="um-record-card-email-value">{record.email}</div>
                                                    <Select
                                                        value={getUserEditValue(record, 'status') || 'Active'}
                                                        onChange={(v) => handleUserFieldChange(record.unique_identifier, 'status', v)}
                                                        className="um-record-card-select"
                                                        bordered={false}
                                                        onClick={(e) => e.stopPropagation()}
                                                        options={[
                                                            { value: 'Active', label: t('admin.userManagement.active') },
                                                            { value: 'Inactive', label: t('admin.userManagement.inactive') },
                                                            { value: 'Disabled', label: t('admin.userManagement.disabled') },
                                                            { value: 'Pending', label: t('admin.userManagement.pending') }
                                                        ]}
                                                    />
                                                    <Select
                                                        value={getUserEditValue(record, 'role_id')}
                                                        onChange={(v) => handleUserFieldChange(record.unique_identifier, 'role_id', v)}
                                                        className="um-record-card-select"
                                                        bordered={false}
                                                        placeholder={t('admin.userManagement.role')}
                                                        onClick={(e) => e.stopPropagation()}
                                                        options={roles.map(r => ({ value: r.id, label: r.name }))}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="um-record-card-body">
                                                    <div className="um-record-card-name">{record.firstname} {record.lastname}</div>
                                                    <div className="um-record-card-email">{record.email}</div>
                                                    <div className="um-record-card-meta">
                                                        <Tag color={(record.status || '').toLowerCase() === 'active' ? 'green' : 'orange'}>{capitalize(record.status)}</Tag>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                    </div>
                                )}
                            </div>
                        </Spin>
                    </>
                ) : (
                    <>
                <div className="um-sidebar">
                    <div className="um-search-row">
                        <Checkbox
                            checked={sortedData.length > 0 && sortedData.every(r => getCurrentSelected().includes(r.unique_identifier))}
                            indeterminate={sortedData.some(r => getCurrentSelected().includes(r.unique_identifier)) && !sortedData.every(r => getCurrentSelected().includes(r.unique_identifier))}
                            onChange={(e) => {
                                if (e.target.checked) {
                                    const setSelection = activeTab === 'users' ? setSelectedUsers : activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
                                    setSelection(sortedData.map(r => r.unique_identifier));
                                } else {
                                    const setSelection = activeTab === 'users' ? setSelectedUsers : activeTab === 'registrations' ? setSelectedRegistrations : setSelectedDeletedUsers;
                                    setSelection([]);
                                }
                            }}
                            className="um-select-all-checkbox"
                        />
                        <Input
                            placeholder={t('admin.userManagement.searchUsers')}
                            prefix={<SearchOutlined />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            className="um-search-input"
                        />
                        {sortDropdown}
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAddUser}
                            className="um-add-btn-inline"
                            aria-label={t('admin.userManagement.newUser')}
                        />
                    </div>

                    <Spin spinning={loading}>
                        <div className="um-user-list">
                            {sortedData.length === 0 ? (
                                <div className="um-empty">
                                    <UserOutlined style={{ fontSize: '48px', color: '#ccc' }} />
                                    <p>{t('admin.userManagement.noUsersFound')}</p>
                                </div>
                            ) : (
                                <>
                                {sortedData.map((record) => (
                                    <div 
                                        key={record.unique_identifier}
                                        className={`um-user-card ${selectedUser?.unique_identifier === record.unique_identifier ? 'selected' : ''}`}
                                        onClick={(e) => handleUserSelect(record, e)}
                                    >
                                        <Checkbox
                                            checked={getCurrentSelected().includes(record.unique_identifier)}
                                            onChange={(e) => handleCheckboxChange(record.unique_identifier, e.target.checked, e)}
                                            onClick={(e) => e.stopPropagation()}
                                            className="um-user-card-checkbox"
                                        />
                                        <Avatar 
                                            style={{ 
                                                backgroundColor: getAvatarColor(record.firstname),
                                                flexShrink: 0
                                            }}
                                            size={48}
                                        >
                                            {getInitials(record.firstname, record.lastname)}
                                        </Avatar>
                                        <div className="um-user-card-info">
                                            <div className="um-user-card-name">
                                                {record.firstname} {record.lastname}
                                            </div>
                                            <div className="um-user-card-email">{record.email}</div>
                                        </div>
                                        <div className="um-user-card-role">
                                            {activeTab === 'users' && (
                                                <>
                                                    <div 
                                                        className="um-status-indicator"
                                                        style={{
                                                            backgroundColor: 
                                                                (record.status || '').toLowerCase() === 'active' ? '#52c41a' : 
                                                                (record.status || '').toLowerCase() === 'inactive' ? '#faad14' : 
                                                                (record.status || '').toLowerCase() === 'disabled' ? '#ff4d4f' : 
                                                                '#d9d9d9'
                                                        }}
                                                    />
                                                    <Tag 
                                                        color={(record.role || '').toLowerCase() === 'admin' ? 'blue' : 'default'}
                                                        className="um-role-tag"
                                                    >
                                                        {capitalize(record.role)}
                                                    </Tag>
                                                </>
                                            )}
                                            {(activeTab === 'registrations' || activeTab === 'deletedUsers') && (
                                                <Tag color={(record.status || '').toLowerCase() === 'active' ? 'green' : 'orange'}>
                                                    {capitalize(record.status)}
                                                </Tag>
                                            )}
                                        </div>
                                    </div>
                                ))}
                                <div className="um-list-spacer" />
                                </>
                            )}
                        </div>
                    </Spin>
                </div>

                {/* Right Panel - User Details */}
                <div className="um-detail-panel">
                    {!selectedUser ? (
                        <div className="um-detail-empty">
                            <UserOutlined style={{ fontSize: '64px', color: '#e0e0e0' }} />
                            <h3>{t('admin.userManagement.selectUser')}</h3>
                            <p>{t('admin.userManagement.selectUserDescription')}</p>
                        </div>
                    ) : (
                        <div className="um-detail-content">
                            {/* User Header */}
                            <div className="um-detail-header">
                                <div className="um-detail-header-left">
                                    <Avatar 
                                        style={{ 
                                            backgroundColor: getAvatarColor(selectedUser.firstname),
                                        }}
                                        size={80}
                                    >
                                        {getInitials(selectedUser.firstname, selectedUser.lastname)}
                                    </Avatar>
                                    <div className="um-detail-header-info">
                                        <h2>{selectedUser.firstname} {selectedUser.lastname}</h2>
                                        <p>{selectedUser.email}</p>
                                    </div>
                                </div>
                                <div className="um-detail-header-right">
                                    <div className="um-detail-header-meta">
                                        {selectedUser.created_at}
                                    </div>
                                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                        {activeTab === 'users' && (
                                            <>
                                                <Tag color={(selectedUser.role || '').toLowerCase() === 'admin' ? 'blue' : 'default'}>
                                                    {capitalize(selectedUser.role)}
                                                </Tag>
                                                <Tag 
                                                    color={
                                                        (selectedUser.status || '').toLowerCase() === 'active' ? 'green' : 
                                                        (selectedUser.status || '').toLowerCase() === 'inactive' ? 'orange' : 
                                                        (selectedUser.status || '').toLowerCase() === 'disabled' ? 'red' : 
                                                        'default'
                                                    }
                                                >
                                                    {capitalize(selectedUser.status) || 'Active'}
                                                </Tag>
                                            </>
                                        )}
                                        {(activeTab === 'registrations' || activeTab === 'deletedUsers') && (
                                            <Tag 
                                                color={(selectedUser.status || '').toLowerCase() === 'active' ? 'green' : (selectedUser.status || '').toLowerCase() === 'pending' ? 'orange' : 'red'}
                                            >
                                                {capitalize(selectedUser.status)}
                                            </Tag>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Account Details - contact card layout */}
                            <div className="um-detail-section">
                                <div className="um-contact-info-banner">
                                    <div className="um-email-banner">
                                        <div className="um-email-banner-value" title={selectedUser.email}>{selectedUser.email}</div>
                                    </div>
                                    <div className="um-contact-info-fields">
                                        <div className="um-detail-item-stacked">
                                            {activeTab === 'users' ? (
                                                <Input 
                                                    value={editFormData.firstname}
                                                    onChange={(e) => handleFieldChange('firstname', e.target.value)}
                                                    placeholder={t('admin.userManagement.firstName')}
                                                    className="um-detail-input"
                                                    bordered={false}
                                                />
                                            ) : (
                                                <div className="um-detail-value">{selectedUser.firstname || '-'}</div>
                                            )}
                                        </div>
                                        <div className="um-detail-item-stacked">
                                            {activeTab === 'users' ? (
                                                <Input 
                                                    value={editFormData.lastname}
                                                    onChange={(e) => handleFieldChange('lastname', e.target.value)}
                                                    placeholder={t('admin.userManagement.lastName')}
                                                    className="um-detail-input"
                                                    bordered={false}
                                                />
                                            ) : (
                                                <div className="um-detail-value">{selectedUser.lastname || '-'}</div>
                                            )}
                                        </div>
                                        {activeTab === 'users' && (
                                            <>
                                                <div className="um-detail-item-stacked">
                                                    <Select
                                                        value={editFormData.status}
                                                        onChange={(value) => handleFieldChange('status', value)}
                                                        style={{ width: '100%' }}
                                                        className="um-detail-select"
                                                        bordered={false}
                                                        placeholder={t('admin.userManagement.status')}
                                                    >
                                                        <Select.Option value="Active">{t('admin.userManagement.active')}</Select.Option>
                                                        <Select.Option value="Inactive">{t('admin.userManagement.inactive')}</Select.Option>
                                                        <Select.Option value="Disabled">{t('admin.userManagement.disabled')}</Select.Option>
                                                        <Select.Option value="Pending">{t('admin.userManagement.pending')}</Select.Option>
                                                    </Select>
                                                </div>
                                                <div className="um-detail-item-stacked">
                                                    <Select
                                                        value={editFormData.role_id}
                                                        onChange={(value) => {
                                                            const selectedRole = roles.find(r => r.id === value);
                                                            setEditFormData(prev => ({
                                                                ...prev,
                                                                role_id: value,
                                                                role: selectedRole ? selectedRole.name.toLowerCase() : ''
                                                            }));
                                                        }}
                                                        style={{ width: '100%' }}
                                                        className="um-detail-select"
                                                        bordered={false}
                                                        placeholder={t('admin.userManagement.role')}
                                                    >
                                                        {roles.map(role => (
                                                            <Select.Option key={role.id} value={role.id}>
                                                                {role.name}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}
                </div>
                    </>
                )}
            </div>

            <AddUserModal
                visible={addUserModalVisible}
                onClose={() => { setAddUserModalVisible(false); setEditingUser(null); }}
                onUserCreated={handleUserCreated}
                onUserUpdated={() => { fetchUsers(true); setEditingUser(null); }}
                roles={roles}
                editingUser={editingUser}
            />
        </div>
    );
};

export default UserManagement; 